import React, { useEffect, useState, useCallback } from 'react';
import { apiFetchAuthed } from '../apiHelpers';
import { Link } from 'react-router-dom';
import { DataTable } from '../components/table/DataTable';
import PageLayout from '../components/ui/PageLayout';
import { buttonClass } from '../components/ui/Button';
import { Field } from '../components/ui/Field';
import Tooltip from '../components/ui/Tooltip';
import { Alert } from '../components/ui/Alert';
import EmptyState from '../components/ui/EmptyState';
import LogoSpinner from '../components/ui/LogoSpinner';
import Reveal from '../components/ui/Reveal';
import { Panel, StatTile } from '../components/ui/Panel';

// Module scope so the spaces table's cells can call it: it closes over nothing,
// and a per-render copy would be one more identity for no gain.
const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        + ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

// ---------------------------------------------------------------------------
// The GitBook spaces table, as shared-DataTable columns.
//
// Module scope per the DataTable contract: the array never changes identity, so
// the label editor keeps its DOM node and its caret while somebody types. Every
// volatile thing a cell needs — which row is being edited, the draft label,
// which spaces are mid-sync, and all six handlers — arrives through `meta`,
// which the page rebuilds every render on purpose.
//
// No `sortable` and no accessorFn: the list is short and comes back in the
// server's order, and nothing here asked for a sort.
// ---------------------------------------------------------------------------
const SPACE_COLUMNS = [
    {
        id: 'space_id',
        header: 'Space ID',
        cell: ({ row }) => (
            <code className="text-xs text-on-surface bg-surface-container-highest px-1.5 py-0.5 rounded">
                {row.original.space_id}
            </code>
        ),
    },
    {
        id: 'label',
        header: 'Label',
        // Click-to-edit in place. NEVER memoize this cell: tanstack memoizes the
        // context object it receives, so the render that picks up a new draft is
        // the table's own — a memoized cell would skip it and paint a stale one.
        cell: ({ row, table }) => {
            const m = table.options.meta;
            const space = row.original;
            if (m.editingId === space.space_id) {
                return (
                    <div className="flex gap-1">
                        <Field
                            type="text"
                            size="sm"
                            aria-label="Space label"
                            value={m.editLabel}
                            onChange={(e) => m.onEditLabelChange(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && m.onSaveLabel(space.space_id)}
                            className="!w-32"
                            autoFocus
                        />
                        <button onClick={() => m.onSaveLabel(space.space_id)} className={buttonClass('quiet', 'sm')}>Save</button>
                        <button onClick={m.onEditCancel} className={buttonClass('quiet', 'sm')}>Cancel</button>
                    </div>
                );
            }
            // A real button, not a clickable span: the only way in was a mouse
            // click, so a keyboard could not reach the editor and a screen
            // reader announced nothing. Preflight already strips a button's
            // margin, padding and font, so `text-left` is the one UA default
            // (text-align: center) that still needs undoing.
            return (
                <button
                    type="button"
                    className="text-xs text-on-surface-variant cursor-pointer hover:text-on-surface text-left rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
                    onClick={() => m.onEditStart(space)}
                >
                    {space.label || <span className="italic text-on-surface-disabled">click to label</span>}
                </button>
            );
        },
    },
    {
        id: 'pages',
        header: 'Pages',
        meta: { align: 'center', numeric: true, cellClass: 'text-xs text-on-surface' },
        cell: ({ row }) => row.original.page_count,
    },
    {
        id: 'last_synced',
        header: 'Last Synced',
        meta: { cellClass: 'text-xs text-on-surface-variant' },
        cell: ({ row }) => formatDate(row.original.last_synced_at),
    },
    {
        id: 'status',
        header: 'Status',
        meta: { align: 'center' },
        cell: ({ row, table }) => {
            const m = table.options.meta;
            const space = row.original;
            if (space.last_sync_error) {
                return <span className="text-red-400 text-xs" title={space.last_sync_error}>Error</span>;
            }
            if (m.syncing[space.space_id]) {
                return <span className="text-yellow-400 text-xs">Syncing...</span>;
            }
            return (
                <button
                    onClick={() => m.onToggleEnabled(space)}
                    className={`text-xs px-2 py-0.5 rounded-full ${
                        space.enabled
                            ? 'bg-green-500/10 text-green-400 border border-green-500/20'
                            : 'bg-gray-700/50 text-on-surface-variant border border-outline-variant'
                    }`}
                >
                    {space.enabled ? 'Enabled' : 'Disabled'}
                </button>
            );
        },
    },
    {
        id: 'actions',
        header: 'Actions',
        meta: { align: 'right' },
        cell: ({ row, table }) => {
            const m = table.options.meta;
            const space = row.original;
            return (
                <div className="flex items-center justify-end gap-2">
                    <Tooltip label="Sync this space">
                        <button
                            onClick={() => m.onSync(space.space_id)}
                            disabled={m.syncing[space.space_id]}
                            className={buttonClass('quiet', 'sm')}
                        >
                            Sync
                        </button>
                    </Tooltip>
                    <Tooltip label="Delete space and pages">
                        <button
                            onClick={() => m.onDelete(space.space_id)}
                            className={buttonClass('danger-quiet', 'sm')}
                        >
                            Delete
                        </button>
                    </Tooltip>
                </div>
            );
        },
    },
];

const AIControls = () => {
    const [spaces, setSpaces] = useState([]);
    const [stats, setStats] = useState(null);
    const [docStatus, setDocStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState({}); // { spaceId: true } while syncing
    const [syncAllRunning, setSyncAllRunning] = useState(false);
    const [newSpaceId, setNewSpaceId] = useState('');
    const [newSpaceLabel, setNewSpaceLabel] = useState('');
    const [addError, setAddError] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editLabel, setEditLabel] = useState('');

    const fetchAll = useCallback(async () => {
        try {
            const [spacesData, statsData, statusData] = await Promise.all([
                apiFetchAuthed('/ai/docs/spaces'),
                apiFetchAuthed('/ai/stats').catch(() => null),
                apiFetchAuthed('/ai/docs/status'),
            ]);
            if (spacesData) setSpaces(spacesData);
            if (statsData) setStats(statsData);
            if (statusData) setDocStatus(statusData);
        } catch (err) {
            console.error('Failed to load AI controls:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const handleAddSpace = async () => {
        const sid = newSpaceId.trim();
        if (!sid) return;
        setAddError('');
        try {
            const space = await apiFetchAuthed('/ai/docs/spaces', {
                method: 'POST',
                body: JSON.stringify({ space_id: sid, label: newSpaceLabel.trim() || null }),
            });
            if (space) {
                setSpaces(prev => [...prev, { ...space, page_count: 0 }]);
                setNewSpaceId('');
                setNewSpaceLabel('');
            }
        } catch (err) {
            setAddError(err.message || 'Failed to add space');
        }
    };

    const handleDeleteSpace = async (spaceId) => {
        if (!window.confirm(`Delete space ${spaceId} and all its synced pages?`)) return;
        try {
            await apiFetchAuthed(`/ai/docs/spaces/${spaceId}`, {
                method: 'DELETE',
                skipJsonParse: true,
            });
            setSpaces(prev => prev.filter(s => s.space_id !== spaceId));
        } catch (err) {
            alert('Failed to delete: ' + (err.message || 'Unknown error'));
        }
    };

    const handleToggleEnabled = async (space) => {
        try {
            const updated = await apiFetchAuthed(`/ai/docs/spaces/${space.space_id}`, {
                method: 'PATCH',
                body: JSON.stringify({ enabled: !space.enabled }),
            });
            if (updated) {
                setSpaces(prev => prev.map(s => s.space_id === space.space_id ? { ...s, ...updated } : s));
            }
        } catch (err) {
            console.error('Toggle failed:', err);
        }
    };

    const startEditLabel = (space) => {
        setEditingId(space.space_id);
        setEditLabel(space.label || '');
    };

    const cancelEditLabel = () => setEditingId(null);

    const handleSaveLabel = async (spaceId) => {
        try {
            const updated = await apiFetchAuthed(`/ai/docs/spaces/${spaceId}`, {
                method: 'PATCH',
                body: JSON.stringify({ label: editLabel.trim() || null }),
            });
            if (updated) {
                setSpaces(prev => prev.map(s => s.space_id === spaceId ? { ...s, ...updated } : s));
            }
        } catch (err) {
            console.error('Label update failed:', err);
        }
        setEditingId(null);
    };

    const handleSyncSpace = async (spaceId) => {
        setSyncing(prev => ({ ...prev, [spaceId]: true }));
        try {
            await apiFetchAuthed('/ai/docs/sync', {
                method: 'POST',
                body: JSON.stringify({ space_ids: [spaceId] }),
            });
            // Poll for completion after a delay
            setTimeout(async () => {
                const spacesData = await apiFetchAuthed('/ai/docs/spaces');
                if (spacesData) setSpaces(spacesData);
                setSyncing(prev => ({ ...prev, [spaceId]: false }));
            }, 5000);
        } catch (err) {
            console.error('Sync failed:', err);
            setSyncing(prev => ({ ...prev, [spaceId]: false }));
        }
    };

    const handleSyncAll = async () => {
        setSyncAllRunning(true);
        try {
            await apiFetchAuthed('/ai/docs/sync', {
                method: 'POST',
                body: JSON.stringify({}),
            });
            // Poll for completion
            setTimeout(async () => {
                await fetchAll();
                setSyncAllRunning(false);
            }, 15000);
        } catch (err) {
            console.error('Sync all failed:', err);
            setSyncAllRunning(false);
        }
    };

    if (loading) {
        return <LogoSpinner label="Loading AI controls" />;
    }

    return (
        <PageLayout kind="workspace" className="pt-6 text-on-surface">
            <>
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                    <Link to="/admin" className="text-on-surface-variant hover:text-on-surface transition-colors">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M12 4L6 10L12 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </Link>
                    <h1 className="text-3xl font-bold">AI Controls</h1>
                </div>

                {/* Usage Stats */}
                <Reveal show={!!stats}>
                {stats && (
                    <div className="mb-8">
                        <h2 className="text-lg font-semibold text-yellow-400 mb-3">Usage Overview</h2>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <StatCard label="Conversations" value={stats.conversations} />
                            <StatCard label="User Messages" value={stats.messages.user} />
                            <StatCard label="Total Tokens" value={stats.messages.total_tokens.toLocaleString()} />
                            <StatCard label="Avg Response" value={`${stats.messages.avg_response_ms}ms`} />
                            <StatCard label="SQL Queries" value={stats.queries.total} />
                            <StatCard label="Failed Queries" value={stats.queries.failed} tone={stats.queries.failed > 0 ? 'danger' : undefined} />
                            <StatCard label="Self-Corrections" value={stats.queries.retries} />
                            <StatCard label="Active Users" value={stats.active_users} />
                        </div>
                        <p className="text-xs text-on-surface-variant mt-2">Model: <span className="text-on-surface">{stats.model}</span></p>
                    </div>
                )}
                </Reveal>

                {/* Documentation Status */}
                <Reveal show={!!docStatus}>
                {docStatus && (
                    <div className="mb-8">
                        <h2 className="text-lg font-semibold text-yellow-400 mb-3">Documentation Index</h2>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            <StatCard label="Indexed Pages" value={docStatus.page_count} />
                            <StatCard label="Last Synced" value={formatDate(docStatus.last_synced)} small />
                            <StatCard label="Spaces" value={spaces.filter(s => s.enabled).length} />
                        </div>
                    </div>
                )}
                </Reveal>

                {/* GitBook Spaces */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="text-lg font-semibold text-yellow-400">GitBook Spaces</h2>
                        <button
                            onClick={handleSyncAll}
                            disabled={syncAllRunning}
                            className={buttonClass('tonal', 'sm')}
                        >
                            {syncAllRunning ? 'Syncing All...' : 'Sync All Enabled'}
                        </button>
                    </div>

                    {/* Add space form */}
                    <p className="text-[11px] text-on-surface-variant mb-2">
                        Find the Space ID in the GitBook URL: <code className="text-on-surface-variant bg-surface-container px-1 rounded">app.gitbook.com/o/.../s/<span className="text-yellow-400/70">sCDLcJrn8yHLadQxxMGQ</span></code>
                    </p>
                    <div className="flex gap-2 mb-4">
                        <Field
                            type="text"
                            aria-label="Space ID"
                            value={newSpaceId}
                            onChange={(e) => setNewSpaceId(e.target.value)}
                            placeholder="Space ID (e.g. sCDLcJrn8yHLadQxxMGQ)"
                            className="flex-1"
                        />
                        <Field
                            type="text"
                            aria-label="Space label"
                            value={newSpaceLabel}
                            onChange={(e) => setNewSpaceLabel(e.target.value)}
                            placeholder="Label (optional)"
                            className="!w-48"
                        />
                        <button
                            onClick={handleAddSpace}
                            disabled={!newSpaceId.trim()}
                            className={buttonClass('filled')}
                        >
                            Add
                        </button>
                    </div>
                    <Reveal show={!!addError}><div className="pb-3"><Alert tone="error" variant="text">{addError}</Alert></div></Reveal>

                    {/* Spaces table. No wrapper box: the shared table renders
                        its own vessel, and the volatile half of the label editor
                        rides `meta` so the columns above can stay at module
                        scope and the input can keep its caret. */}
                    <DataTable
                        data={spaces}
                        columns={SPACE_COLUMNS}
                        getRowId={(space) => space.space_id}
                        meta={{
                            editingId,
                            editLabel,
                            syncing,
                            onEditStart: startEditLabel,
                            onEditLabelChange: setEditLabel,
                            onEditCancel: cancelEditLabel,
                            onSaveLabel: handleSaveLabel,
                            onToggleEnabled: handleToggleEnabled,
                            onSync: handleSyncSpace,
                            onDelete: handleDeleteSpace,
                        }}
                        empty={
                            <EmptyState variant="area">
                                No spaces configured. Add a GitBook space ID above.
                            </EmptyState>
                        }
                    />
                </div>

                {/* Other AI Controls */}
                <div>
                    <h2 className="text-lg font-semibold text-yellow-400 mb-3">Configuration</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <ConfigCard label="AI Model" value={stats?.model || 'Unknown'} />
                        <ConfigCard label="Rate Limit" value="10 msg/min per user, 50 global/min" />
                        <ConfigCard label="Max Message Length" value="10,000 characters" />
                        <ConfigCard label="SQL Row Limit" value="500 rows" />
                    </div>
                </div>
            </>
        </PageLayout>
    );
};

const StatCard = ({ label, value, tone, small }) => (
    <StatTile label={label} value={value} tone={tone} size={small ? 'sm' : 'md'} />
);

const ConfigCard = ({ label, value }) => (
    <Panel label={label} padding="sm">
        <div className="flex items-center justify-between gap-3 px-1 py-0.5">
            <span className="text-sm text-on-surface-variant">{label}</span>
            <span className="text-sm text-on-surface font-mono">{value}</span>
        </div>
    </Panel>
);

export default AIControls;
